'use client';

import { useEffect, useState, useCallback, type ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from '@/lib/store';
import { fetchActiveBlogs } from '@/lib/store/slices/blogSlice';
import { useIntersection, revealClasses } from '@/lib/hooks/useIntersection';
import { Pagination } from '@/components/ui/Pagination';
import { FilterBar } from '@/components/sections/FilterBar';
import CategoryChips from '@/components/listing/CategoryChips';
import type { ListingHero } from '@/components/listing/types';
import { SkeletonCard } from '@/components/ui/Skeleton';
import { BlogCard } from '@/components/ui/BlogCard';
import { useTranslations } from 'next-intl';
import type { FilterValue } from '@/components/sections/FilterBar';
import type { Blog, Category } from '@/types';

interface BlogClientProps {
  locale: string;
  initialPosts?: Blog[];
  /** Server-rendered intro paragraph shown under the page heading. */
  intro?: ReactNode;
  /** All active blog categories (server-fetched) for the filter chips. */
  categories?: Category[];
  /** Set on a category landing page: only that category's posts are listed. */
  category?: Category | null;
  /** Replaces the generic page heading (category pages use the category's name + description). */
  hero?: ListingHero;
}

export default function BlogClient({ locale, initialPosts = [], intro, categories = [], category = null, hero }: BlogClientProps) {
  const t        = useTranslations('Blog');

  const sortOptions = [
    { value: '-publishedAt', label: t('sortNewest')   },
    { value: 'publishedAt',  label: t('sortOldest')   },
    { value: '-readingTime', label: t('sortLongest')  },
    { value: 'readingTime',  label: t('sortShortest') },
  ];
  const dispatch = useDispatch<AppDispatch>();
  const { items: reduxBlogs, loading, total, page, pages, limit, error } = useSelector((s: RootState) => s.blogs);
  const { ref, visible } = useIntersection<HTMLElement>();

  // The redux slice is shared with the homepage featured preview — only show its
  // contents once THIS view's own fetch has succeeded, otherwise we flash another
  // page's posts (or a filtered subset) as the initial state.
  const [loaded, setLoaded] = useState(false);

  const [filters, setFilters] = useState<FilterValue>({
    page: 1, limit: 9, sort: '-publishedAt',
    // The API resolves the slug (either language) to the category.
    ...(category ? { category: category.slug.en } : {}),
  });

  const blogs = loaded ? reduxBlogs : initialPosts;

  useEffect(() => {
    let active = true;
    dispatch(fetchActiveBlogs(filters)).then(action => {
      if (active && fetchActiveBlogs.fulfilled.match(action)) setLoaded(true);
    });
    return () => { active = false; };
  }, [dispatch, filters]);

  const retry = useCallback(() => {
    dispatch(fetchActiveBlogs(filters)).then(action => {
      if (fetchActiveBlogs.fulfilled.match(action)) setLoaded(true);
    });
  }, [dispatch, filters]);

  const handleFilterChange = useCallback((newFilters: FilterValue) => {
    setFilters(newFilters);
  }, []);

  return (
    <>
      {/* Page hero */}
      <section className="py-16 px-4 sm:px-6 text-center">
        <p className="text-xs font-mono text-primary-500 tracking-widest uppercase mb-3">{hero?.eyebrow ?? t('eyebrow')}</p>
        <h1 className="text-4xl sm:text-5xl font-bold text-(--text) mb-4">{hero?.title ?? t('heading')}</h1>
        {(hero ? hero.description : t('desc')) && (
          <p className="text-lg text-(--muted) max-w-2xl mx-auto">{hero ? hero.description : t('desc')}</p>
        )}
        {intro && <p className="mt-5 text-base text-(--muted) leading-relaxed max-w-3xl mx-auto">{intro}</p>}
      </section>

      {/* Category filter: links to server-rendered category pages */}
      <CategoryChips locale={locale} section="blog" categories={categories} activeSlug={category?.slug.en} />

      {/* Search / sort */}
      <FilterBar
        sortOptions={sortOptions}
        locale={locale}
        value={filters}
        onChange={handleFilterChange}
        searchPlaceholder={t('searchPlaceholder') ?? 'Search articles…'}
        // Before this view's own fetch lands, a category page already knows its size from the server.
        total={loaded ? total : category?.count}
      />

      {/* Grid */}
      <section ref={ref} className="section-padding px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">

          {/* Load failure — keep any posts already on screen, offer a retry */}
          {error && (
            <div
              role="alert"
              className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-500/30 bg-red-500/5 px-4 py-3"
            >
              <span className="text-sm text-(--text)">{t('loadError')}</span>
              <button
                type="button"
                onClick={retry}
                disabled={loading}
                className="text-sm font-medium text-primary-500 hover:text-primary-400 transition-colors disabled:opacity-60 cursor-pointer"
              >
                {t('retry')}
              </button>
            </div>
          )}

          {loading && blogs.length === 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: filters.limit ?? 9 }).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          ) : blogs.length === 0 ? (
            <p className="text-center text-(--muted) py-20">{t('empty')}</p>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {blogs.map((post, i) => (
                  <BlogCard
                    key={post._id}
                    post={post}
                    locale={locale}
                    labels={{ readMore: t('readMore'), min: t('min') }}
                    className={revealClasses(visible, Math.min(i * 80, 400))}
                  />
                ))}
              </div>

              <div className="mt-8">
                <Pagination
                  page={page}
                  total={total}
                  limit={limit}
                  pages={pages}
                  onPageChange={p => setFilters(f => ({ ...f, page: p }))}
                />
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}
